import { displayPatientName } from './patientName';
// 별칭 사전·표준키 접기는 공통 정본으로 이관됨 (9월 묶음 #1, 2026-08-06)
// 정본: dentcluster-agent/shared/statement-alias.js → 사본 ./shared/statement-alias.js (sync-shared.js 생성)
// 기존 import 경로를 깨지 않기 위해 재수출 유지.
import { normKey, canonKey, detectMaterialInName } from './shared/statement-alias';
import { designSrcOf } from './designState';   // 디자인 완료/전 배지 근거 (2026-09-06)
export { normKey, canonKey, detectMaterialInName } from './shared/statement-alias';

// 거래명세서 항목 생성·단가 매칭 공용 로직
// 작성 화면(CreateTransactionStatement)과 일괄 draft 생성(TransactionStatementList)이
// 반드시 같은 로직을 쓰도록 분리 — 금액 계산이 화면마다 달라지는 것을 방지한다.

// 날짜를 밀리초 타임스탬프로 변환 (Firestore Timestamp / Date / 문자열 혼용 흡수)
export const getTimestamp = (dateValue) => {
    if (!dateValue) return 0;
    if (dateValue.seconds !== undefined) {
        return dateValue.seconds * 1000 + (dateValue.nanoseconds || 0) / 1000000;
    }
    if (typeof dateValue.toDate === 'function') {
        return dateValue.toDate().getTime();
    }
    if (dateValue instanceof Date) {
        return dateValue.getTime();
    }
    return new Date(dateValue).getTime();
};

// ── 표준 명칭 사전 (2026-07-30 대표 확정 — 기획서 docs/의뢰서표준명칭_거래처현황_기획서_2026-07-30.md 2절)
// 보철물+재료를 한글 한 단어로. 의뢰서 선택지·명세서 표기 공용. key = 보철canon|재료canon
export const STANDARD_NAMES = [
    // 크라운
    { name: '지르코니아 크라운', prosthesis: 'crown', material: 'zirconia' },
    { name: '이맥스 크라운', prosthesis: 'crown', material: 'emax' },
    { name: 'PFM 크라운', prosthesis: 'crown', material: 'pfm' },
    { name: '골드 크라운', prosthesis: 'crown', material: 'gold' },
    { name: '메탈 크라운', prosthesis: 'crown', material: 'metal' },
    { name: 'PMMA 크라운(임시)', prosthesis: 'crown', material: 'pmma' },
    { name: '컴퍼지트 크라운', prosthesis: 'crown', material: 'composite' },
    { name: '티타늄 크라운', prosthesis: 'crown', material: 'titanium' },
    // 인레이·온레이 (별칭 사전상 onlay는 inlay로 접힘 — 한 묶음)
    { name: '지르코니아 인레이', prosthesis: 'inlay', material: 'zirconia' },
    { name: '이맥스 인레이', prosthesis: 'inlay', material: 'emax' },
    { name: '골드 인레이', prosthesis: 'inlay', material: 'gold' },
    { name: '컴퍼지트 인레이', prosthesis: 'inlay', material: 'composite' },
    // 비니어
    { name: '이맥스 비니어', prosthesis: 'veneer', material: 'emax' },
    // 2026-07-30 대표 승인 추가 4종 (실사용 단축어 흡수: hybrid inlay·metal inlay·서베이드·포스트)
    { name: '하이브리드 인레이', prosthesis: 'inlay', material: 'hybrid' },
    { name: '메탈 인레이', prosthesis: 'inlay', material: 'metal' },
    { name: '서베이드 크라운', prosthesis: 'surveyed', material: '' },
    { name: '포스트', prosthesis: 'post', material: '' },
    { name: '지르코니아 비니어', prosthesis: 'veneer', material: 'zirconia' },
    // 폰틱 — 선택지 아님(브릿지 자동 생성)이지만 표시는 한글로
    { name: '폰틱', prosthesis: 'pontic', material: '' },
    // 왁스업
    { name: '왁스업', prosthesis: 'wax', material: '' },
    { name: '왁스업', prosthesis: '', material: 'wax' },
    // 임플란트 (종류 무관 통일)
    { name: '임플란트', prosthesis: 'implant', material: '' },
];
const _stdIndex = (() => {
    const m = new Map();
    for (const s of STANDARD_NAMES) {
        m.set(s.prosthesis + '|' + s.material, s.name);
        if (s.material && !m.has(s.prosthesis + '|')) { /* 보철만 있는 폴백은 아래 한글 라벨로 */ }
    }
    return m;
})();
// canon → 한글 보철명 (조합이 사전에 없을 때 폴백)
const KOREAN_PROSTHESIS = { crown: '크라운', pontic: '폰틱', inlay: '인레이', veneer: '비니어', implant: '임플란트', denture: '덴쳐', wax: '왁스업', surveyed: '서베이드 크라운', post: '포스트' };
const KOREAN_MATERIAL = { zirconia: '지르코니아', composite: '컴퍼지트', titanium: '티타늄', emax: '이맥스', metal: '메탈', pfm: 'PFM', pmma: 'PMMA', gold: '골드', wax: '왁스', hybrid: '하이브리드' };

// 화면 표기용 한글 명칭 (2026-07-30 대표 확정: 파일이 영문으로 적어도 한국어로 표시)
// 매칭용 serviceName(원문)은 절대 건드리지 않는다 — 표기 전용
export const koreanDisplayName = (prosthesis, material) => {
    const pk = canonKey(prosthesis);
    const mk = canonKey(material);
    // 매칭 별칭이 접어버리는 종류(코핑→크라운, 온레이→인레이)는 표기만 되살림
    const override = /coping/i.test(prosthesis || '') ? '코핑'
        : /onlay/i.test(prosthesis || '') ? '온레이' : '';
    if (override) {
        const m0 = KOREAN_MATERIAL[mk] || (material || '');
        return m0 ? `${m0} ${override}` : override;
    }
    const std = _stdIndex.get(pk + '|' + mk);
    if (std) return std;
    const p = KOREAN_PROSTHESIS[pk] || (prosthesis || '');
    const m = KOREAN_MATERIAL[mk] || (material || '');
    if (pk === 'implant') return '임플란트';
    if (p && m) return `${m} ${p}`;
    return p || m || '';
};

// 단가표 항목 분류 (2026-07-30 대표 확정: 단가표 화면 보철물/재료/임플란트 3구역)
// 명시 category가 있으면 우선, 없으면 별칭 표준키로 추정
const MATERIAL_CANONS = new Set(['zirconia', 'composite', 'titanium', 'emax', 'metal', 'pfm', 'pmma', 'gold', 'wax', 'hybrid']);
export const priceItemCategory = (item) => {
    if (item && (item.category === 'prosthesis' || item.category === 'material' || item.category === 'implant')) return item.category;
    const ck = canonKey(item && item.serviceName);
    if (ck === 'implant') return 'implant';
    if (MATERIAL_CANONS.has(ck)) return 'material';
    return 'prosthesis';
};

// 단가표 매칭
// 1) 의뢰서의 보철물 명칭(치과 단축어)과 정확 일치 — 재료까지 일치 우선, 다음 명칭만 일치
// 2) 폴백: 부분 포함 매칭(긴 이름 우선 — "Gold Crown"이 "Crown"보다 먼저)
export const findPriceMatch = (priceList, toothInfo, serviceName, material) => {
    const list = priceList.filter(p => p.serviceName);
    const sn = normKey(serviceName);
    if (sn) {
        const mat = normKey(material);
        if (mat) {
            const exactBoth = list.find(p => normKey(p.serviceName) === sn && normKey(p.material) === mat);
            if (exactBoth) return exactBoth;
        }
        // 재료 미지정 단가(공통 단가) → 명칭 일치 아무거나 순
        const exactGeneric = list.find(p => normKey(p.serviceName) === sn && !normKey(p.material));
        if (exactGeneric) return exactGeneric;
        const exactAny = list.find(p => normKey(p.serviceName) === sn);
        if (exactAny) return exactAny;
    }
    // 2.5) 별칭 표준키 일치 — "AnatomicCrown" vs "지르크라운" 등 CAD·수기 표기 차이 흡수 (재료 일치 우선)
    // 2026-07-30 보강: "하이브리드 인레이" vs "메탈 인레이"처럼 보철 표준키가 같아도
    // 이름에 박힌 재료가 다르면 다른 단가 — 이름 속 재료까지 비교해 오매칭 차단
    const ck = canonKey(serviceName);
    if (ck) {
        const matOf = (p) => canonKey(p.material) || detectMaterialInName(p.serviceName);
        const imat = canonKey(material) || detectMaterialInName(serviceName);
        const rowsCk = list.filter(p => canonKey(p.serviceName) === ck);
        if (imat) {
            const both = rowsCk.find(p => matOf(p) === imat);
            if (both) return both;
            // 재료 무관(공통) 단가만 폴백 — 다른 재료의 단가를 집어오지 않는다
            const generic = rowsCk.find(p => !matOf(p));
            if (generic) return generic;
        } else {
            const generic = rowsCk.find(p => !matOf(p));
            if (generic) return generic;
            if (rowsCk.length === 1) return rowsCk[0];
        }
    }
    // 2.7) 재료 단독 단가 폴백 (2026-07-30) — 보철물 명칭 매칭이 없으면 재료 구역 단가로
    // (예: 단가표에 '지르코니아'만 등록 → Zirconia - monolithic 크라운·인레이 전부 그 단가)
    {
        const cmat = canonKey(material);
        if (cmat) {
            const matEntry = list.find(p => canonKey(p.serviceName) === cmat);
            if (matEntry) return matEntry;
        }
    }
    const text = (toothInfo || '').toLowerCase();
    return [...list]
        .sort((a, b) => b.serviceName.length - a.serviceName.length)
        .find(p => text.includes(p.serviceName.toLowerCase()) || (canonKey(p.serviceName) && canonKey(text).includes(canonKey(p.serviceName))));
};

// 단가표 문서 items 정규화 — 앱(name/price)과 웹(serviceName/unitPrice) 필드 호환
export const normalizePriceItems = (rawItems) => (rawItems || []).map(item => ({
    serviceName: item.serviceName || item.name || '',
    unitPrice: item.unitPrice ?? (Number(item.price) || 0),
    material: item.material || '',
    source: item.source || 'manual',
    // category 통과 (2026-09-01 검증 P2): 정규화가 category를 탈락시키면 이름으로 추론 불가한
    // 구데이터(재료/임플란트 구역에 넣었던 브랜드명 등)가 재저장 시 보철로 드리프트 — 명시값 보존.
    ...(item.category ? { category: item.category } : {}),
    name: item.name || item.serviceName || '',
    price: item.price ?? item.unitPrice ?? 0,
}));

// 명세서 번호 생성
export const generateStatementNumber = () => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const rand = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    return `TS-${dateStr}-${rand}`;
};

// 리메이크 원 건 id (2026-10-24 2단계 ④ — 계약 §5): 재제작 줄이 "어느 건을 다시 만든 것인지" 잃지 않게 넘긴다.
//   수집분(에이전트) = parsedCases.remakeOfId → remakeOfCaseId (복사 의뢰서에 실린 값 우선, 통합 뷰 케이스(_case)는 원본에서)
//   웹 의뢰서(치과 작성) = remakeOfOrderId 그대로. 리메이크가 아니거나 값이 없으면 키를 만들지 않는다.
//   _remakeOfDateHint = 치과가 의뢰서에서 고른 원 건 날짜(ms) — 화면 표시용 임시값, 저장하지 않는다(serialize 화이트리스트 밖).
export const remakeLinkOf = (order) => {
    if (!order || !order.isRemake) return {};
    const out = {};
    const caseId = order.remakeOfCaseId || (order._case && order._case.remakeOfId) || null;
    if (caseId) out.remakeOfCaseId = caseId;
    if (order.remakeOfOrderId) out.remakeOfOrderId = order.remakeOfOrderId;
    if (order.remakeOfOrderDate) {
        const t = getTimestamp(order.remakeOfOrderDate);
        if (t) out._remakeOfDateHint = t;
    }
    return out;
};

// 의뢰서 목록 → 거래명세서 항목 생성
// 같은 보철물+재료 조합은 치아번호를 묶어 한 행으로. priceList가 있으면 단가 자동 적용(priceSource: 'auto')
// 임플란트는 보철물과 별도 행으로 분리 청구 — 같은 치아에 "보철물(단축어) 행 + 임플란트(브랜드) 행" 2행 생성
// (기존엔 임플란트가 보철물을 대체해 임플란트 단가가 누락됐음. 2026-07-19)
export const buildItemsFromOrders = (orders, { priceList = [] } = {}) => {
    const newItems = [];

    orders.forEach((order) => {
        // 에이전트 수집 의뢰서는 환자명에 섞인 쉐이드/재료 표기를 정제해 품목명에 사용
        // (정제 전에 등록된 구버전 agent_auto 의뢰서 대응 — 이미 정제된 이름엔 변화 없음)
        const orderPatientName = order.source === 'agent_auto'
            ? displayPatientName(order).name
            : (order.patientName || '');
        if (order.items && Array.isArray(order.items) && order.items.length > 0) {
            // 같은 보철물+재료 조합으로 그룹화
            const groups = {};
            const addToGroup = (key, meta, toothNumber) => {
                if (!groups[key]) groups[key] = { ...meta, toothNumbers: [] };
                if (toothNumber) groups[key].toothNumbers.push(toothNumber);
            };

            order.items.forEach((item) => {
                // 보철물과 임플란트가 같은 것을 가리키면(3shape은 어버트먼트가 양쪽에 기록됨)
                // 임플란트 행 하나만 생성 — 별칭 병합으로 둘 다 '임플란트' 단가가 잡혀 이중청구되는 것 방지
                const sameAsImplant = !!item.implant && !!item.prosthesis &&
                    canonKey(item.prosthesis) === canonKey(item.implant);
                // 1) 보철물(치과 단축어 — 재료 포함 문자열일 수 있음) 행
                if (item.prosthesis && !sameAsImplant) {
                    addToGroup(`${item.prosthesis}|${item.material || '-'}`, {
                        prosthesis: item.prosthesis,
                        material: item.material || '-',
                        serviceKey: item.prosthesis,   // 단가표 정확 매칭용 (단축어 원문)
                    }, item.toothNumber);
                }
                // 2) 임플란트 행 (보철물과 별도 청구)
                if (item.implant) {
                    // 임플란트 회사·시스템(implantSystem — exocad constructionInfo·3Shape 주문, 에이전트 alpha.120~ / 대표 10-31 "임플란트 종류만 거래명세서에")
                    //   표시에만 붙인다. 매칭 키(serviceKey)는 그대로라 기존 단가표 매칭은 바뀌지 않는다
                    const sys = String(item.implantSystem || '').trim();
                    addToGroup(`임플란트 ${item.implant}|-|${sys}`, {
                        prosthesis: `임플란트 ${item.implant}`,
                        material: '-',
                        serviceKey: item.implant,      // 단가표 매칭 = 브랜드명
                        implantSystem: sys,
                    }, item.toothNumber);
                }
                // 3) 보철물·임플란트 둘 다 없는 항목(단독 폰틱 등) — 기존처럼 '-' 행
                if (!item.prosthesis && !item.implant) {
                    addToGroup(`-|${item.material || '-'}`, {
                        prosthesis: '-',
                        material: item.material || '-',
                        serviceKey: '',
                    }, item.toothNumber);
                }
            });

            Object.entries(groups).forEach(([, group], groupIndex) => {
                const sortedTeeth = group.toothNumbers
                    .map(n => parseInt(n))
                    .filter(n => !isNaN(n))
                    .sort((a, b) => a - b);

                const teethStr = sortedTeeth.length > 0 ? `#${sortedTeeth.join(',')}` : '#-';
                // 파일 수집분은 CAD 영문 표기를 한글 표준 명칭으로 표시 (2026-07-30 대표 확정)
                // 치과가 직접 쓴 단축어(웹 의뢰서)는 원문 유지 — 매칭용 serviceName도 항상 원문
                let label = `${group.prosthesis} ${group.material}`;
                if (order.source === 'agent_auto') {
                    const kr = koreanDisplayName(group.prosthesis, group.material === '-' ? '' : group.material);
                    if (kr) label = kr;
                }
                if (group.implantSystem) label = `${label} (${group.implantSystem})`;
                const toothInfo = `${teethStr} ${label}`;
                // P1-2 (2026-08-20 대표 확정): 치식 없는 품목(덴처·교합장치·수리 등)은 수량 0으로 생성돼
                // 단가를 넣어도 합계(수량×단가)가 0원에 고착됐다 — 새로 만드는 항목은 치아 0개면 수량 1로 시작.
                // 저장된 기존 명세서는 재계산하지 않으므로 하위호환 무영향.
                const toothCount = sortedTeeth.length > 0 ? sortedTeeth.length : 1;
                // 재제작/수리 구분 (2026-09-04 대표 승인): 수리는 환자명 [수리] + items[].remakeKind='repair'(화면 배지로 표시).
                // toothInfo에는 "(수리)" 접미를 넣지 않는다 — findPriceMatch 폴백이 toothInfo를 보므로 단가 제안 오염 방지(검토 C-5).
                const isRepair = !!order.isRemake && order.remakeKind === 'repair';
                const remakeLabel = order.isRemake ? (isRepair ? ' [수리]' : ' [리메이크]') : '';

                newItems.push({
                    id: `${order.id}-${groupIndex}-${Date.now()}-${Math.random()}`,
                    orderId: order.id,
                    // 측정 1단계 (2026-08-17 대표 확정): 원본 파싱 케이스 id를 항목에 직접 기록.
                    // 파싱 기반 오더(agent_auto)만 값이 있고, 수기/일반 오더는 null.
                    // ※ raw parsedCaseId 직접기록까지만 — 정본 dedup·상하악/이중소스 병합은 이번 범위 아님(#31 별건).
                    parsedCaseId: order.parsedCaseId ?? null,
                    source: order.source === 'agent_auto' ? 'auto' : 'order',
                    // 10-31 대표: 수집분(agent_auto)은 작성일·마감일 칸을 비운다(직접 입력) — 웹 의뢰서는 그대로. 기간·정렬·묶음은 디자인 완성일(designDate)
                    createdAt: order.source === 'agent_auto' ? null : order.createdAt,
                    dueDate: order.source === 'agent_auto' ? null : (order.dueDate ?? null),
                    designDate: order.designDate || order.createdAt || null,
                    patientName: orderPatientName + remakeLabel,
                    toothInfo: toothInfo,
                    toothCount: toothCount,
                    serviceName: group.serviceKey || '',
                    material: group.material === '-' ? '' : (group.material || ''),
                    isRemake: order.isRemake || false,
                    remakeKind: order.isRemake ? (order.remakeKind || '') : '',
                    remakeReason: order.remakeReason || '',
                    ...remakeLinkOf(order),   // 원 건 id (2026-10-24 ④)
                    unitPrice: 0,
                    _designSrc: designSrcOf(order),   // 디자인 완료/전 배지 근거 (로드된 객체에서 그대로, 추가 조회 없음)
                    _sortTimestamp: getTimestamp(order.designDate || order.createdAt)
                });
            });
        } else {
            // items가 없는 경우 기본 항목 생성
            const isRepair = !!order.isRemake && order.remakeKind === 'repair';
            const remakeLabel = order.isRemake ? (isRepair ? ' [수리]' : ' [리메이크]') : '';

            newItems.push({
                id: `${order.id}-${Date.now()}-${Math.random()}`,
                orderId: order.id,
                // 측정 1단계 (2026-08-17): 파싱 케이스 id 직접 기록 (없으면 null)
                parsedCaseId: order.parsedCaseId ?? null,
                source: order.source === 'agent_auto' ? 'auto' : 'order',
                createdAt: order.source === 'agent_auto' ? null : order.createdAt,   // 10-31 수집분 작성일·마감일 비움
                dueDate: order.source === 'agent_auto' ? null : (order.dueDate ?? null),
                designDate: order.designDate || order.createdAt || null,
                patientName: orderPatientName + remakeLabel,
                toothInfo: '-',
                toothCount: 1,   // P1-2 (2026-08-20): 치식 없는 항목도 수량 1로 시작 — 단가 입력 시 0원 고착 방지
                serviceName: '',
                material: '',
                isRemake: order.isRemake || false,
                remakeKind: order.isRemake ? (order.remakeKind || '') : '',
                remakeReason: order.remakeReason || '',
                ...remakeLinkOf(order),   // 원 건 id (2026-10-24 ④)
                unitPrice: 0,
                _designSrc: designSrcOf(order),   // 디자인 완료/전 배지 근거 (로드된 객체에서 그대로, 추가 조회 없음)
                _sortTimestamp: getTimestamp(order.designDate || order.createdAt)
            });
        }
    });

    // 단가표 자동 매칭 (단축어 정확 일치 우선, 부분 포함 폴백)
    //
    // 재제작(리메이크)은 자동으로 채우지 않는다 — 대표 확정 2026-08-11(b안).
    //   무상 재제작인지 유상인지는 사안마다 다른데, 종전에는 다른 항목과 똑같이 단가가 붙어
    //   기공소가 손으로 0원을 만들지 않으면 재제작이 그대로 청구됐다.
    //   단가를 비워 두고 '무상 여부 확인'만 표시한다 — 유상이면 사람이 직접 넣는다.
    if (priceList.length > 0) {
        newItems.forEach(item => {
            if (item.isRemake) {
                const match = findPriceMatch(priceList, item.toothInfo, item.serviceName, item.material);
                item.unitPrice = 0;
                item.needsPriceCheck = true;                       // 화면에서 '무상 여부 확인' 표시
                if (match) item.suggestedUnitPrice = match.unitPrice;   // 유상이면 넣을 값 (자동 적용은 안 함)
                return;
            }
            const match = findPriceMatch(priceList, item.toothInfo, item.serviceName, item.material);
            if (match) {
                item.unitPrice = match.unitPrice;
                item.priceSource = 'auto';   // 단가표 자동 적용 표시 (학습 대상 아님)
            }
        });
    }

    return newItems;
};

// 리메이크 책임 구분 (계약 §1 — 기공소 내부 기록만)
export const REMAKE_LIABILITIES = ['lab', 'clinic', 'shared', 'unknown'];
// 책임 기록 문서(statementRemakeNotes)의 줄 키 — statementItemKeyOf(utils/statementView.js)와 같은 4개 값(orderId·patientName·toothInfo·serviceName)을
// 해시한 값. 문서 안 키에 환자 이름이 그대로 들어가지 않게 한다(FNV-1a 32비트 2회 = 16자리 hex, 보안 해시 아님 — 줄 맞춤용).
const fnv1a = (str, seed) => {
    let h = seed >>> 0;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
};
export const remakeNoteKeyOf = (item) => {
    const raw = JSON.stringify([
        item?.orderId ?? null, item?.patientName || '', item?.toothInfo || '', item?.serviceName || item?.name || '',
    ]);
    return `k${fnv1a(raw, 0x811c9dc5)}${fnv1a(raw, 0x01234567)}`;
};

// remakeOf 표시값 정리 — date(YYYY-MM-DD) 필수, statementNumber 선택. 그 밖의 키(이름 등)는 버린다.
export const cleanRemakeOf = (v) => {
    if (!v || typeof v !== 'object') return null;
    const date = typeof v.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.date) ? v.date : '';
    if (!date) return null;
    return { date, ...(v.statementNumber ? { statementNumber: String(v.statementNumber) } : {}) };
};
// 인쇄·치과 화면용 짧은 표기: "재제작 · 처음 작업 10/3 · 명세서 TS-20261005-012" (수리 줄은 "수리 · …", remakeOf 없으면 '')
//   용어(2026-10-24 대표): '원 건' → '처음 작업' — 치과 종이에도 나가는 글자
export const remakeOfLabel = (item) => {
    const r = cleanRemakeOf(item && item.remakeOf);
    if (!r) return '';
    const [, m, d] = r.date.split('-');
    return `${item.remakeKind === 'repair' ? '수리' : '재제작'} · 처음 작업 ${Number(m)}/${Number(d)}${r.statementNumber ? ` · 명세서 ${r.statementNumber}` : ''}`;
};

// 저장용 항목 직렬화 — 임시저장·발행·일괄 draft 모두 동일 스키마 (단가/합계 별칭 동기화 포함)
export const serializeStatementItems = (items) => items.map(item => ({
    orderId: item.orderId ?? null,
    // 측정 1단계 (2026-08-17 대표 확정): 원본 파싱 케이스 id를 저장 스키마에 보존.
    // 발행분 누락 감지를 이름 퍼지매칭 없이 정확히 하기 위한 직접 추적 필드.
    // 파싱 기반 항목만 값이 있고 수기/일반 항목·구건은 null(옵셔널). 금액·발행·정산 로직과 무관.
    parsedCaseId: item.parsedCaseId ?? null,
    createdAt: item.createdAt ?? null,
    dueDate: item.dueDate ?? null,
    designDate: item.designDate ?? null,   // 디자인 완성일(10-31) — 다시 열 때 묶음·정렬 유지
    patientName: item.patientName || '',
    toothInfo: item.toothInfo || '',
    toothCount: item.toothCount || 0,
    countManual: item.countManual || false,   // 개수 수동입력 보호 플래그 보존
    source: item.source || 'order',           // 자동수집/수기 출처 보존
    serviceName: item.serviceName || '',      // 단가표 정확 매칭·학습용 단축어
    material: item.material || '',
    priceSource: item.priceSource || '',      // auto=단가표 적용 / manual=직접 입력
    isRemake: item.isRemake || false,
    remakeKind: item.remakeKind || '',        // 'remake' | 'repair' | '' (2026-09-04) — 표시용, 금액 계산 무관
    remakeReason: item.remakeReason || '',
    // 재제작 무상확인 상태 보존 (2026-08-13 검토자 지적 P1-4): 종전엔 저장 시 누락돼
    // 재로드하면 '무상 여부 확인' 표시와 제안단가가 사라져 리메이크가 0원인지 유상인지 알 수 없었다.
    needsPriceCheck: item.needsPriceCheck || false,
    suggestedUnitPrice: item.suggestedUnitPrice ?? null,
    // ── 리메이크 연결 (2026-10-24 2단계 ④ — 계약 §5) ── 전부 옵셔널, 값 없으면 키를 쓰지 않는다(골드 필드와 같은 방식).
    // remakeOfCaseId/remakeOfOrderId = 원 건 문서 id(내부 추적) · remakeOf = 표시용 {date:'YYYY-MM-DD', statementNumber?} (환자명 없음)
    // 책임 구분(remakeLiability)은 여기 저장하지 않는다 (검수 P2-7 — '기공소만 보기'를 데이터에서도 지킨다):
    //   명세서 문서는 치과도 읽으므로 statementRemakeNotes/{statementId}(기공소 회사만 읽기·쓰기)에 따로 둔다. 옛 문서의 값은 읽을 때만 호환.
    // remakeUnlinked = 사람이 [연결 끊기]를 누름 → 저장 때 원 건 표기를 자동으로 다시 채우지 않는다 (검수 P2-8)
    ...(item.remakeOfCaseId ? { remakeOfCaseId: String(item.remakeOfCaseId) } : {}),
    ...(item.remakeOfOrderId ? { remakeOfOrderId: String(item.remakeOfOrderId) } : {}),
    ...(!item.remakeUnlinked && cleanRemakeOf(item.remakeOf) ? { remakeOf: cleanRemakeOf(item.remakeOf) } : {}),
    ...(item.remakeUnlinked ? { remakeUnlinked: true } : {}),
    // ── 골드(금값) 파생 행 필드 (2026-08-20 대표 확정 — 기획서 docs/기획서_명세서_골드입력_개선_2026-08.md 2부) ──
    // 전부 옵셔널: 없는 기존 데이터에는 키 자체를 쓰지 않아 재저장 시에도 diff가 생기지 않는다.
    // itemType 'gold' = 금값 파생 행(면세, 개수1×단가라 기존 합계·앱·세금계산서 계산식 무수정 통과)
    // goldLineId = 부모 공임 행의 연결 토큰 / goldForItemId = 금값 행이 가리키는 부모의 goldLineId
    ...(item.itemType ? { itemType: item.itemType } : {}),
    ...(item.goldLineId ? { goldLineId: item.goldLineId } : {}),
    ...(item.goldForItemId ? { goldForItemId: item.goldForItemId } : {}),
    ...(item.goldWeight !== undefined && item.goldWeight !== null ? { goldWeight: item.goldWeight } : {}),
    ...(item.goldPricePerGram !== undefined && item.goldPricePerGram !== null ? { goldPricePerGram: item.goldPricePerGram } : {}),
    // 디자인 완료/전 (2026-09-06 대표 지시): 'done'|'pending'|'unknown'. 작성 화면이 저장 직전에 채운다.
    // 옵셔널 — 없는 구데이터·금값 행은 키를 쓰지 않는다(열람 화면은 값 없으면 배지 생략). 매칭 키·단가 로직 무관.
    ...(item.designState ? { designState: item.designState } : {}),
    unitPrice: item.unitPrice || 0,
    pricePerTooth: item.unitPrice || 0,
    totalPrice: (item.toothCount || 0) * (item.unitPrice || 0),
    price: (item.toothCount || 0) * (item.unitPrice || 0)
}));
